import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Target, TrendingUp, Receipt, CreditCard, Headphones, Zap, ArrowRight, Check, Sun, Moon,
  Users, Bot, Workflow, BarChart3, MessageSquare, ShieldCheck, Sparkles, Menu, Star,
  FileSpreadsheet, Mail, MessageCircle, Building2, Phone,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const LIFECYCLE = ["Lead", "Sales", "Quote", "Invoice", "Payment", "Service", "Support", "Renewal"];

const FEATURES = [
  { icon: Target, title: "Intelligent CRM", desc: "Capture every lead from Meta, WhatsApp, forms & calls into one Lead Engine with scoring and SLAs.", tone: "text-primary bg-primary/10" },
  { icon: TrendingUp, title: "Sales Pipeline", desc: "Visual pipelines, forecasting, deal health and a Sales Command Center for managers.", tone: "text-info bg-info/10" },
  { icon: Receipt, title: "GST Invoicing", desc: "Professional GST-ready quotes & invoices with partial payments and automatic receipts.", tone: "text-success bg-success/10" },
  { icon: CreditCard, title: "Online Payments", desc: "Razorpay, Cashfree & PayU adapters with verified webhooks and idempotent processing.", tone: "text-automation bg-automation/10" },
  { icon: Workflow, title: "Service Workflows", desc: "Turn won deals into service cases with department handoffs, SOPs and evidence.", tone: "text-escalation bg-escalation/10" },
  { icon: Headphones, title: "Support & SLA", desc: "Tickets, escalations, response & resolution SLAs with a full SLA engine.", tone: "text-critical bg-critical/10" },
  { icon: Users, title: "Team Live", desc: "Real-time employee status, shifts, wrap-up and workload visibility.", tone: "text-primary bg-primary/10" },
  { icon: Zap, title: "War Room", desc: "One-click command center with live metrics, bottleneck detection and drill-down.", tone: "text-info bg-info/10" },
  { icon: Bot, title: "AI Assistant", desc: "Ask what to focus on, which deals are at risk, and why sales are down — grounded in your data.", tone: "text-automation bg-automation/10" },
];

const INDUSTRIES = ["Recruitment", "Logistics", "Real Estate", "IT Services", "Digital Agencies", "Education", "Consulting", "Manufacturing", "Travel", "Retail", "Professional Services"];

const PLANS = [
  { name: "Starter", monthly: 1499, users: "Up to 5 users", features: ["CRM, Leads & Contacts", "Basic Sales Pipeline", "Tasks & Follow-ups", "Quotations & Invoices", "Basic Support & Reports"] },
  { name: "Growth", monthly: 3999, users: "Up to 15 users", popular: true, features: ["Everything in Starter", "Meta Lead Ads & routing", "WhatsApp + Razorpay/Cashfree/PayU", "Payment automation & receipts", "Customer Portal & Team Live"] },
  { name: "Professional", monthly: 7999, users: "Up to 35 users", features: ["Advanced workflows & approvals", "SOPs, checklists & QC", "SLA escalation", "Shift & workload management", "War Room & Advanced Analytics"] },
  { name: "Business", monthly: 14999, users: "Up to 75 users", features: ["Multiple Business Units", "Advanced Automation & AI", "API, Webhooks & Audit Logs", "Advanced Permissions", "Customer Health & War Room+"] },
];

const FAQS = [
  { q: "Is Blueconnect multi-tenant and secure?", a: "Yes. Every company is an isolated tenant with strict data isolation, role-based access control, audit logs and encrypted credentials. One tenant can never access another's data." },
  { q: "Which payment gateways are supported?", a: "Razorpay, Cashfree and PayU. Each company connects its own gateway credentials; secrets stay server-side and webhooks are signature-verified and idempotent." },
  { q: "Can I capture leads from Meta and WhatsApp?", a: "Yes. Connect multiple Meta pages, ad campaigns and lead forms. Every source feeds a single Lead Engine with intelligent routing and SLA timers." },
  { q: "Do you offer a free trial?", a: "Every plan starts with a 14-day free trial. No credit card required to explore the full platform with demo data." },
  { q: "Can I upgrade or change plans later?", a: "Absolutely. Usage bars show when you're approaching limits, and upgrading is a single click with instant provisioning." },
];

function ThemeBtn() {
  const { theme, setTheme } = useTheme();
  return (
    <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="landing-theme-toggle">
      <Sun className="h-5 w-5 dark:hidden" />
      <Moon className="hidden h-5 w-5 dark:block" />
    </Button>
  );
}

function DashboardPreview() {
  const bars = [40, 68, 52, 88, 74, 96, 60];
  return (
    <div className="relative rounded-2xl border border-border bg-card p-4 shadow-2xl shadow-primary/10">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogoMark className="h-6 w-6" />
          <span className="font-heading text-sm font-bold">War Room · Live</span>
          <span className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> Real-time
          </span>
        </div>
        <div className="flex gap-1">
          {["bg-critical", "bg-warning", "bg-success"].map((c) => <span key={c} className={`h-2.5 w-2.5 rounded-full ${c}`} />)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { l: "New Leads", v: "128", t: "text-primary" },
          { l: "Pipeline", v: "₹25.3L", t: "text-info" },
          { l: "Revenue", v: "₹8.2L", t: "text-success" },
          { l: "SLA Risk", v: "6", t: "text-warning" },
          { l: "Escalations", v: "3", t: "text-critical" },
          { l: "Online", v: "24", t: "text-automation" },
        ].map((k, i) => (
          <motion.div key={k.l} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.06 }}
            className="rounded-lg border border-border bg-background p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.l}</p>
            <p className={`font-heading text-lg font-bold ${k.t}`}>{k.v}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-3 rounded-lg border border-border bg-background p-3">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-medium">Sales this week</span>
          <span className="text-success">+18%</span>
        </div>
        <div className="flex h-20 items-end gap-1.5">
          {bars.map((h, i) => (
            <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.5 + i * 0.05, duration: 0.5 }}
              className="flex-1 rounded-t bg-gradient-to-t from-primary/60 to-primary" />
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-background p-2.5">
        <div className="flex -space-x-1.5">
          {["bg-primary", "bg-info", "bg-automation", "bg-success"].map((c) => (
            <span key={c} className={`h-6 w-6 rounded-full border-2 border-card ${c}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Team Live · <span className="font-medium text-foreground">24 online</span></p>
        <span className="ml-auto flex items-center gap-1 text-[11px] text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" /> Payment received ₹1,18,000</span>
      </div>
    </div>
  );
}

export default function Landing() {
  const [annual, setAnnual] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);

  const price = (m) => (annual ? Math.round(m * 10) : m); // 2 months free

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-border bc-glass">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <nav className="hidden items-center gap-7 text-sm font-medium text-muted-foreground md:flex">
            <a href="#features" className="transition-colors hover:text-foreground">Platform</a>
            <a href="#lifecycle" className="transition-colors hover:text-foreground">Lifecycle</a>
            <a href="#industries" className="transition-colors hover:text-foreground">Industries</a>
            <a href="#pricing" className="transition-colors hover:text-foreground">Pricing</a>
            <a href="#faq" className="transition-colors hover:text-foreground">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeBtn />
            <Link to="/login" className="hidden sm:block">
              <Button variant="ghost" data-testid="nav-login-btn">Log in</Button>
            </Link>
            <Link to="/signup">
              <Button className="rounded-full" data-testid="nav-signup-btn">Start Free Trial</Button>
            </Link>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileNav((o) => !o)}>
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {mobileNav && (
          <div className="border-t border-border bg-card px-4 py-3 md:hidden">
            {["features", "lifecycle", "industries", "pricing", "faq"].map((a) => (
              <a key={a} href={`#${a}`} onClick={() => setMobileNav(false)} className="block py-2 text-sm capitalize text-muted-foreground">{a}</a>
            ))}
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bc-grid-bg opacity-40" />
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -right-40 top-20 h-96 w-96 rounded-full bg-cyan/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show">
              <Badge variant="secondary" className="mb-5 rounded-full border border-border px-3 py-1 text-xs font-medium">
                <Sparkles className="mr-1.5 h-3.5 w-3.5 text-primary" /> Connect. Sell. Serve. Grow.
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Your Entire Customer <span className="text-primary">Journey.</span> Connected.
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Capture leads, close sales, collect payments, manage after-sales service, empower your teams
              and control your entire business from one intelligent platform.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup"><Button size="lg" className="rounded-full" data-testid="hero-start-trial-btn">Start Free Trial <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
              <Link to="/signup"><Button size="lg" variant="outline" className="rounded-full" data-testid="hero-book-demo-btn">Book a Demo</Button></Link>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-6 flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success" /> Multi-tenant & secure</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-success" /> 14-day free trial</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-success" /> No card required</span>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <DashboardPreview />
          </motion.div>
        </div>
      </section>

      {/* Trusted by */}
      <section className="border-y border-border bg-card/50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-center text-xs font-medium uppercase tracking-widest text-muted-foreground">Built for growing businesses across every industry</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-semibold text-muted-foreground/70">
            {["Certon Technology", "Certon Logistic", "Job Spring", "Kiyaron", "Smart Bidd", "Blueorbit"].map((n) => (
              <span key={n} className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl">Your business is scattered across too many tools</h2>
          <p className="mt-3 text-muted-foreground">Excel, WhatsApp, email, payment gateways, support inboxes and separate task systems — nothing talks to each other.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[FileSpreadsheet, MessageCircle, Mail, CreditCard, Headphones, Phone].map((Icon, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-card p-5 text-muted-foreground">
              <Icon className="h-6 w-6" />
              <span className="text-xs">{["Excel", "WhatsApp", "Email", "Gateways", "Support", "Calls"][i]}</span>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 flex items-center justify-center">
          <div className="flex items-center gap-3 rounded-full border border-primary/30 bg-primary/5 px-6 py-3 font-heading text-lg font-semibold text-primary">
            <Zap className="h-5 w-5" /> Bring everything into Blueconnect.
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section id="lifecycle" className="border-y border-border bg-card/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl">One connected lifecycle</h2>
            <p className="mt-3 text-muted-foreground">From first lead to final service and renewal — automated, tracked and visible.</p>
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {LIFECYCLE.map((step, i) => (
              <React.Fragment key={step}>
                <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold">
                  {step}
                </motion.div>
                {i < LIFECYCLE.length - 1 && <ArrowRight className="h-4 w-4 text-primary" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl">Everything your business needs</h2>
          <p className="mt-3 text-muted-foreground">A complete, connected platform — not another single-purpose tool.</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="group rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40">
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${f.tone}`}>
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="border-y border-border bg-card/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl">Industry-ready templates</h2>
            <p className="mt-3 text-muted-foreground">Start fast with pipelines and workflows tailored to your industry.</p>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5">
            {INDUSTRIES.map((ind, i) => (
              <motion.span key={ind} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 6}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary">
                {ind}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-4xl">Simple, transparent pricing</h2>
          <p className="mt-3 text-muted-foreground">Save up to 2 months with annual billing.</p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2">
            <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} data-testid="pricing-billing-toggle" />
            <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>Annual</span>
            <Badge className="bg-success/15 text-success hover:bg-success/15">Save 2 months</Badge>
          </div>
        </div>
        <div className="mt-12 grid gap-5 lg:grid-cols-4">
          {PLANS.map((p) => (
            <div key={p.name} data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative flex flex-col rounded-2xl border bg-card p-6 ${p.popular ? "border-primary shadow-lg shadow-primary/10" : "border-border"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground">
                  <Star className="mr-1 inline h-3 w-3" /> Most Popular
                </span>
              )}
              <h3 className="font-heading text-lg font-bold">{p.name}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{p.users}</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-heading text-3xl font-extrabold">₹{price(p.monthly).toLocaleString("en-IN")}</span>
                <span className="pb-1 text-xs text-muted-foreground">/{annual ? "year" : "month"}</span>
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="mt-6">
                <Button className="w-full rounded-full" variant={p.popular ? "default" : "outline"} data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                  Start Free Trial
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 text-center">
          <h3 className="font-heading text-lg font-bold">Enterprise</h3>
          <p className="mt-1 text-sm text-muted-foreground">Custom users, dedicated infrastructure, white-label, advanced API, SLA and a dedicated account manager.</p>
          <Link to="/signup"><Button variant="outline" className="mt-4 rounded-full">Contact Sales</Button></Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-border bg-card/40 py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-center font-heading text-2xl font-bold tracking-tight sm:text-4xl">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-primary" />
        <div className="absolute inset-0 bc-grid-bg opacity-20" />
        <div className="relative mx-auto max-w-3xl px-4 text-center text-primary-foreground sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">From first lead to final service</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">Connect your entire customer lifecycle in one intelligent business platform. Grow faster with Blueconnect.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup"><Button size="lg" variant="secondary" className="rounded-full" data-testid="cta-start-trial-btn">Start Your Free Trial</Button></Link>
            <Link to="/signup"><Button size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10">Book a Demo</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <Logo />
              <p className="mt-3 max-w-xs text-sm text-muted-foreground">The all-in-one Business CRM & Customer Lifecycle platform. Connect. Sell. Serve. Grow.</p>
            </div>
            {[
              { h: "Product", l: ["CRM", "Sales", "Billing", "Support", "War Room"] },
              { h: "Company", l: ["About", "Careers", "Partners", "Security", "Contact"] },
              { h: "Resources", l: ["Docs", "API", "Status", "Blog", "Community"] },
            ].map((col) => (
              <div key={col.h}>
                <p className="font-heading text-sm font-semibold">{col.h}</p>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {col.l.map((i) => <li key={i}><a href="#features" className="transition-colors hover:text-foreground">{i}</a></li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row">
            <span>© {new Date().getFullYear()} Blueconnect. All rights reserved.</span>
            <span>Connect. Sell. Serve. Grow.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
